import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PlatformRoles, UserInvitationStatuses, UserStatuses } from '../../../../core/enums/data.enums';
import { MultiSelectComponent } from "../../../../parts/multi-select/multi-select.component";
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { UserSessionService } from '../../../../core/services/user-session.service';
import { AuthorizationService } from '../../../../core/services/authorization/authorization.service';
import { ListConstants } from '../../../../core/constants/lists.constants';
import { ParentFocusDirective } from "../../../../core/directives/parent-focus.directive";
import { LocalStorageKeyConstants } from '../../../../core/constants/constants';
import { UsersFilter } from '../../../../core/filters/users.filter';
import { BaseSearchFilterComponent } from '../../../../core/base-classes/base-search-filter.component';

// Sentinel for the "no user-level role" option in the Role dropdown. Rendered as "User" per DEV-2801.
export const USER_ROLE_FILTER_NONE = 'User' as const;
export type UserRoleFilterOption = PlatformRoles | typeof USER_ROLE_FILTER_NONE;

@Component({
  selector: 'ms-user-search-filter',
  imports: [CommonModule, FormsModule, MultiSelectComponent, FontAwesomeModule, ParentFocusDirective],
  templateUrl: './user-search-filter.component.html'
})
export class UserSearchFilterComponent extends BaseSearchFilterComponent<UsersFilter> implements OnInit {

  public UserStatuses = UserStatuses;
  public selectUserStatusOptions = [UserStatuses.Active, UserStatuses.Suspended, UserStatuses.Inactive, UserStatuses.Deleted];
  public selectInvitationStatusOptions = [UserInvitationStatuses.Invited, UserInvitationStatuses.Accepted, UserInvitationStatuses.Expired];
  // Input field values - these are bound to the actual input fields
  public firstNameInput = '';
  public lastNameInput = '';
  public emailInput = '';

  public selectedUserStatus = signal<UserStatuses[]>([]);
  public selectedInvitationStatus = signal<UserInvitationStatuses[]>([]);
  public selectedUserRole = signal<UserRoleFilterOption[]>([]);

  // Resolved on first fetch so UsersFilter.roleIds can carry seeded IDs rather than hardcoded ones
  // (same reasoning as StaffRoleConstants).
  private resolvedRoleIdsByName = signal<Map<PlatformRoles, number>>(new Map());

  // DEV-2801: User sentinel first, then platform roles least-to-most permissive, mirroring the ticket's AC.
  // Keyed off resolvedRoleIdsByName so a named role is only offered once its id is known — otherwise
  // picking it would save roleIds=[] (filter unresolved out below) and silently drop the restriction.
  // If getRoles fails, only "User" shows, which is honest about what the dropdown can actually filter on.
  public selectUserRoleOptions = computed<UserRoleFilterOption[]>(() => {
    const resolved = this.resolvedRoleIdsByName();
    const namedInOrder: PlatformRoles[] = [
      PlatformRoles.Staff,
      PlatformRoles.StaffManager,
      PlatformRoles.SystemAdministrator
    ];
    return [USER_ROLE_FILTER_NONE, ...namedInOrder.filter(r => resolved.has(r))];
  });

  // Gates the dropdown onto its Loading… placeholder until getRoles resolves (or fails) so a user
  // never clicks a named role whose id hasn't landed yet and silently saves an empty roleIds filter.
  public isRoleOptionsLoading = signal<boolean>(true);

  private authorizationService = inject(AuthorizationService);

  constructor(public userSessionService: UserSessionService) {
    super();
    this.selectedUserStatus.set(this.activeFilter.statuses || [UserStatuses.Active]);
    this.selectedInvitationStatus.set(this.activeFilter.invitationStatuses || []);
  }

  public override get activeFilter(): UsersFilter {
    return this.userSessionService.usersFilter;
  }

  protected override getDefaultFilter(): UsersFilter {
    return ListConstants.USERS_FILTER_DEFAULT();
  }

  // Platform-level list with no org scope — every field in the dropdown is user-selectable.
  protected override get preservedFilterKeys(): (keyof UsersFilter)[] {
    return [];
  }

  protected override get dropdownSelector(): string {
    return '#user-search-filter-dropdown';
  }

  protected override syncStateFromFilter(): void {
    this.selectedUserStatus.set([...(this.activeFilter.statuses || [])]);
    this.selectedInvitationStatus.set([...(this.activeFilter.invitationStatuses || [])]);
    this.selectedUserRole.set(this.roleOptionsFromFilter());
  }

  protected override saveToStorage(): void {
    this.userSessionService.saveToLocalStorage(LocalStorageKeyConstants.USERS_FILTER(), this.activeFilter);
  }

  public async ngOnInit(): Promise<void> {
    await this.loadStaffRoles();
    // Replay a persisted roleIds filter into the dropdown once the id→name map is available.
    this.selectedUserRole.set(this.roleOptionsFromFilter());
    this.onApply();
  }

  // One session-cached fetch via AuthorizationService; shared with Staff Management, so there's no
  // extra network cost here beyond the first page that needs it. The loading flag flips in `finally`
  // so a fetch failure still frees the dropdown — selectUserRoleOptions is keyed off the resolved
  // map, so a failed fetch leaves only the "User" row rather than offering roles whose ids never
  // landed. The next page load retries getRoles and reintroduces them.
  private async loadStaffRoles(): Promise<void> {
    try {
      const result = await this.authorizationService.getRoles(true);
      if (!result.success || !result.data) return;

      const platformRoleNames = new Set<string>(Object.values(PlatformRoles));
      const map = new Map<PlatformRoles, number>();
      result.data.forEach(role => {
        if (platformRoleNames.has(role.name)) {
          map.set(role.name as PlatformRoles, role.roleId);
        }
      });
      this.resolvedRoleIdsByName.set(map);
    } finally {
      this.isRoleOptionsLoading.set(false);
    }
  }

  // Reverses the roleIds + hasRole encoding back into dropdown chips. hasRole=false surfaces the
  // "User" sentinel; unknown ids (ones the name map hasn't resolved yet) are dropped rather than
  // shown as missing chips.
  private roleOptionsFromFilter(): UserRoleFilterOption[] {
    const options: UserRoleFilterOption[] = [];
    if (this.activeFilter.hasRole === false) options.push(USER_ROLE_FILTER_NONE);

    const idToName = new Map<number, PlatformRoles>();
    this.resolvedRoleIdsByName().forEach((id, name) => idToName.set(id, name));

    (this.activeFilter.roleIds ?? []).forEach(id => {
      const name = idToName.get(id);
      if (name !== undefined) options.push(name);
    });
    return options;
  }

  public onUserStatusChange(event: any) {
    this.activeFilter.statuses = event;
    this.saveToStorage();
  }

  public onInvitationStatusChange(event: any) {
    this.activeFilter.invitationStatuses = event;
    this.saveToStorage();
  }

  // 'User' and named roles are mutually exclusive — the API ANDs hasRole and roleIds, so a mixed
  // selection would require a user to simultaneously hold no roles and a matching role (zero results).
  // The side the viewer just added wins; the other is cleared from the chip list. The surviving
  // selection is then split into hasRole=false (for 'User') and resolved role IDs.
  public onUserRoleChange(selected: UserRoleFilterOption[]): void {
    const includesUser = selected.includes(USER_ROLE_FILTER_NONE);
    const namedRoles = selected.filter(r => r !== USER_ROLE_FILTER_NONE) as PlatformRoles[];

    // The multi-select's [(selectedOptions)] binding has already overwritten selectedUserRole before
    // this handler fires, so "previous" has to come from the filter the last change was written to.
    const previouslyHadUser = this.activeFilter.hasRole === false;

    let normalized: UserRoleFilterOption[];
    if (includesUser && namedRoles.length > 0) {
      normalized = previouslyHadUser ? namedRoles : [USER_ROLE_FILTER_NONE];
    } else {
      normalized = selected;
    }

    if (normalized.length !== selected.length) {
      this.selectedUserRole.set(normalized);
    }

    const finalIncludesUser = normalized.includes(USER_ROLE_FILTER_NONE);
    const finalRoles = normalized.filter(r => r !== USER_ROLE_FILTER_NONE) as PlatformRoles[];
    const idMap = this.resolvedRoleIdsByName();
    const ids = finalRoles
      .map(name => idMap.get(name))
      .filter((id): id is number => id !== undefined);

    this.activeFilter.hasRole = finalIncludesUser ? false : undefined;
    this.activeFilter.roleIds = ids;
    this.saveToStorage();
  }

  public override setFilterCount(): void {
    const roleChipCount = (this.activeFilter.roleIds?.length ?? 0) +
      (this.activeFilter.hasRole === false ? 1 : 0);
    this.filterCount = (this.activeFilter.firstNames?.length || 0) +
      (this.activeFilter.lastNames?.length || 0) +
      (this.activeFilter.emailAddresses?.length || 0) +
      (this.activeFilter.statuses?.length || 0) +
      (this.activeFilter.invitationStatuses?.length || 0) +
      roleChipCount;
  }

  public addFirstName(): void {
    if (this.firstNameInput && !(this.firstNameInput.trim() === '')) {
      this.activeFilter.firstNames?.push(this.firstNameInput);
      this.saveToStorage();
    }
    this.firstNameInput = '';
  }

  public removeFirstName(index: number): void {
    this.activeFilter.firstNames?.splice(index, 1);
    this.saveToStorage();
  }

  public addLastName(): void {
    if (this.lastNameInput && !(this.lastNameInput.trim() === '')) {
      this.activeFilter.lastNames?.push(this.lastNameInput);
      this.saveToStorage();
    }
    this.lastNameInput = '';
  }

  public removeLastName(index: number): void {
    this.activeFilter.lastNames?.splice(index, 1);
    this.saveToStorage();
  }

  public addEmail(): void {
    if (this.emailInput && !(this.emailInput.trim() === '')) {
      this.activeFilter.emailAddresses?.push(this.emailInput);
      this.saveToStorage();
    }
    this.emailInput = '';
  }

  public removeEmail(index: number): void {
    this.activeFilter.emailAddresses?.splice(index, 1);
    this.saveToStorage();
  }

  public removeStatus(index: number): void {
    this.activeFilter.statuses?.splice(index, 1);
    this.saveToStorage();
  }
}
